/* eslint-disable sonarjs/no-nested-template-literals */
import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CommissionsBonusDetailListRepsonse,
  CommissionsDetailsResponse,
  CommissionsDetailsViewResponse,
  MonthlyCommissionEarningResponse,
} from '@/types/Commissions';
import { ResponseError } from '@/types/Error';
import { SuccessResponse } from '@/types/Success';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';

const applicationJson = 'application/json';

export const commissionsApi = createApi({
  reducerPath: 'commissionsApi',
  keepUnusedDataFor: 300,
  baseQuery: generateBaseQuseryWithReAuth({ basePath: 'commission' }),
  tagTypes: [''],
  endpoints: builder => ({
    getMonthlyCommissionEarningList: builder.query<
      SuccessResponse<MonthlyCommissionEarningResponse>,
      { start: number; limit: number; orderColumn: number; orderDir: 'asc' | 'desc'; searchKey?: string }
    >({
      query: ({ start, limit, orderColumn, orderDir, searchKey }) => ({
        url: `/GetMonthlyCommission?start=${start}&limit=${limit}&ordercolumn=${orderColumn}&orderdir=${orderDir}&downloadexcelfile=false&draw=1&${
          searchKey ? `&searchKey=${searchKey}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<MonthlyCommissionEarningResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getMonthlyCommissionEarningListDownloadUrl: builder.query<
      SuccessResponse<String>,
      {
        searchKey?: string;
      }
    >({
      query: ({ searchKey }) => ({
        // eslint-disable-next-line sonarjs/no-nested-template-literals
        url: `/GetMonthlyCommission?downloadexcelfile=true${
          searchKey ? `&searchKey=${encodeURIComponent(searchKey)}` : ''
        }`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<String>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getCommissionDetailsView: builder.query<SuccessResponse<CommissionsDetailsViewResponse>, void>({
      query: () => ({
        url: `/GetCommissionDetailsView`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<CommissionsDetailsViewResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getCommissionDetails: builder.query<SuccessResponse<CommissionsDetailsResponse>, { runid: number }>({
      query: ({ runid }) => ({
        url: `/GetCommissionDetails${runid ? `?runid=${runid}` : ''}`,
        method: 'GET',
        headers: { 'Content-Type': applicationJson },
      }),
      transformResponse: (response: SuccessResponse<CommissionsDetailsResponse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getRealtimeBonusDetailList: builder.mutation<
      SuccessResponse<CommissionsBonusDetailListRepsonse>,
      { periodid: string; filters: any }
    >({
      query: ({ periodid, filters }) => ({
        url: `/GetRealTimeBonusDetails?periodid=${periodid}`,
        method: 'POST',
        headers: { 'Content-Type': applicationJson },
        body: filters,
      }),
      transformResponse: (response: SuccessResponse<CommissionsBonusDetailListRepsonse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    getHistoricalBonusDetailList: builder.mutation<
      SuccessResponse<CommissionsBonusDetailListRepsonse>,
      { runid: string; filters: any }
    >({
      query: ({ runid, filters }) => ({
        url: `/GetHistoricalBonusDetails?runid=${runid}`,
        method: 'POST',
        headers: { 'Content-Type': applicationJson },
        body: filters,
      }),
      transformResponse: (response: SuccessResponse<CommissionsBonusDetailListRepsonse>) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
  }),
});

export const {
  useGetMonthlyCommissionEarningListQuery,
  useLazyGetMonthlyCommissionEarningListDownloadUrlQuery,
  useGetCommissionDetailsViewQuery,
  useGetCommissionDetailsQuery,
  useGetRealtimeBonusDetailListMutation,
  useGetHistoricalBonusDetailListMutation,
} = commissionsApi;
